import React, { useEffect, useMemo, useState } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, TableContainer, Box, Center, Spinner, Text, Flex, Image, Select, Tfoot } from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import ReactPaginate from "react-paginate";
import MyDiv from "./CustomTable.style";
import RightArrowIcon from "../../assets/Images/arrow-right.svg";
import LeftArrowIcon from "../../assets/Images/arrow-left.svg";

// ---------------- Column Definition ----------------
export interface ColumnProps<T> {
  field?: keyof T | string;
  header?: React.ReactNode;
  body?: (rowData: T, index: number) => React.ReactNode;
  sortable?: boolean;
  textAlign?: "left" | "center" | "right";
  style?: React.CSSProperties;
}

export function Column<T>(_props: ColumnProps<T>) {
  return null;
}

// ---------------- Table Props ----------------
interface CustomTableProps<T> {
  value: T[];
  columns?: ColumnProps<T>[];
  children?: React.ReactNode;
  loading?: boolean;
  emptyMessage?: string;
  emptyImage?: string;
  headerBg?: string;
  headerTextColor?: string;
  striped?: boolean;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  onPageChange?: (e: { skip: number; limit: number }) => void;
  totalRecords?: number;
  pagination?: boolean;
  showEmptyMessage?: boolean;
  rowStyle?: (row: T, index: number) => React.CSSProperties;
  rowClassName?: (row: T, index: number) => string;
  getRowBgColor?: (row: T) => string | undefined;
  footer?: T | null;
}

// ---------------- Main Component ----------------
export function CustomTable<T extends Record<string, any>>({ value, columns, children, loading = false, emptyMessage = "No data found", showEmptyMessage = true, emptyImage = "https://cdn-icons-png.flaticon.com/512/4076/4076500.png", headerBg = "#F9FAFB", headerTextColor = "#667085", striped = true, rowsPerPage = 10, rowsPerPageOptions = [10, 20, 50, 100], onPageChange, totalRecords = 0, pagination = true, rowStyle, rowClassName, getRowBgColor, footer }: CustomTableProps<T>) {
  // Resolve columns
  const resolvedColumns: ColumnProps<T>[] = useMemo(() => {
    if (columns && columns.length) return columns;
    return React.Children.toArray(children)
      .filter((child): child is React.ReactElement<ColumnProps<T>> => React.isValidElement(child) && child.type === Column)
      .map((col) => col.props);
  }, [columns, children]);

  // Sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const sortedData = useMemo(() => {
    if (!sortField || !sortOrder) return value;
    return [...value].sort((a, b) => {
      const valA = a[sortField as string];
      const valB = b[sortField as string];
      if (valA == null || valB == null) return 0;
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [value, sortField, sortOrder]);

  const handleSort = (field?: string) => {
    if (!field) return;
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : sortOrder === "desc" ? null : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(rowsPerPage);

  const actualTotalRecords = onPageChange ? totalRecords : value.length;
  const pageCount = Math.ceil(actualTotalRecords / currentRowsPerPage);
  const start = actualTotalRecords === 0 ? 0 : currentPage * currentRowsPerPage + 1;
  const end = Math.min((currentPage + 1) * currentRowsPerPage, actualTotalRecords);

  const handlePageClick = (event: { selected: number }) => {
    const newSkip = event.selected * currentRowsPerPage;
    setCurrentPage(event.selected);
    onPageChange?.({ skip: newSkip, limit: currentRowsPerPage });
  };

  const handleRowsChange = (val: number) => {
    setCurrentRowsPerPage(val);
    setCurrentPage(0);
    onPageChange?.({ skip: 0, limit: val });
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [actualTotalRecords]);

  const paginatedData = useMemo(() => {
    if (!pagination || onPageChange) {
      return sortedData;
    }
    const startIndex = currentPage * currentRowsPerPage;
    return sortedData.slice(startIndex, startIndex + currentRowsPerPage);
  }, [sortedData, pagination, onPageChange, currentPage, currentRowsPerPage]);

  return (
    <MyDiv>
      {/* Table Section */}
      <Box position="relative" bg="transparent">
        <TableContainer bg="transparent" borderRadius="12px" overflowX="auto" boxShadow="sm" border="1px solid #edeeee">
          {!loading && value.length === 0 ? (
            <Flex direction="column" justify="center" align="center" height="200px">
              <Text className="font-poppins text_md text_semibold" color="gray.500">
                {emptyMessage}
              </Text>
            </Flex>
          ) : (
            <Table variant="simple" size="sm">
              <Thead bg={headerBg} className="table_header font-poppins text_medium text_sm">
                <Tr className="font-poppins text_regular text_md">
                  {resolvedColumns.map((col, idx) => (
                    <Th className="font-poppins text_xsm text_medium" key={idx} fontSize="14px" color={headerTextColor} cursor={col.sortable ? "pointer" : "default"} textAlign={col.textAlign || "left"} style={col.style} onClick={() => col.sortable && handleSort(col.field as string)}>
                      <Flex align="center" gap={1} justify={col.textAlign === "center" ? "center" : col.textAlign === "right" ? "flex-end" : "flex-start"}>
                        {col.header}
                        {col.sortable && sortField === col.field && (sortOrder === "asc" ? <TriangleUpIcon boxSize={3} /> : sortOrder === "desc" ? <TriangleDownIcon boxSize={3} /> : null)}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              </Thead>

              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={resolvedColumns.length}>
                      <Flex justify="center" align="center" height="200px">
                        <Spinner thickness="4px" speed="0.65s" color="#0052CC" size="xl" />
                      </Flex>
                    </Td>
                  </Tr>
                ) : (
                  // ) :  value.length === 0 && showEmptyMessage ? (
                  //   <Tr>
                  //     <Td colSpan={resolvedColumns.length}>
                  //       <Flex
                  //         direction="column"
                  //         justify="center"
                  //         align="center"
                  //         height="300px"
                  //         color="gray.500"
                  //       >
                  //         {/* <Image
                  //           src={emptyImage}
                  //           boxSize="100px"
                  //           mb={3}
                  //           opacity={0.8}
                  //         /> */}

                  //         <Text fontSize="15px">
                  //           {emptyMessage}
                  //         </Text>
                  //       </Flex>
                  //     </Td>
                  //   </Tr>
                  paginatedData.map((row, rowIndex) => {
                    const displayIndex = onPageChange ? rowIndex : currentPage * currentRowsPerPage + rowIndex;
                    return (
                      <Tr
                        key={rowIndex}
                        style={rowStyle?.(row, displayIndex)}
                        bg={getRowBgColor ? getRowBgColor(row) || "#FFFFFF" : "#FFFFFF"}
                        _hover={{
                          bg: getRowBgColor && getRowBgColor(row) ? getRowBgColor(row) : striped ? "#F9FAFB" : "transparent",
                        }}
                        className={`font-poppins text_regular text_md ${rowClassName?.(row, displayIndex) || ""}`}
                      >
                        {resolvedColumns.map((col, colIndex) => (
                          <Td key={colIndex} fontSize="14px" color="inherit" className="font-poppins text_sm text_medium" textAlign={col.textAlign || "left"} style={col.style}>
                            {col.body ? col.body(row, displayIndex) : (row as any)[col.field as string]}
                          </Td>
                        ))}
                      </Tr>
                    );
                  })
                )}
              </Tbody>
              {!loading && footer && (
                <Tfoot>
                  <Tr bg="#F5F3FF">
                    {resolvedColumns.map((col, colIndex) => (
                      <Td key={colIndex} fontSize="14px" fontWeight="700" borderTop="2px solid #D6BBFB" className="font-poppins text_sm text_medium" textAlign={col.textAlign || "left"} style={col.style}>
                        {col.body ? col.body(footer, 0) : (footer as any)[col.field as string]}
                      </Td>
                    ))}
                  </Tr>
                </Tfoot>
              )}
            </Table>
          )}
        </TableContainer>
      </Box>

      {/* Pagination */}
      {pagination && (
        <Flex justify="center" align="center" mt={4} gap={4}>
          <ReactPaginate
            previousLabel={
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Image src={LeftArrowIcon} alt="Prev" width={15} height={15} />
                Prev
              </span>
            }
            nextLabel={
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                Next
                <Image src={RightArrowIcon} alt="Next" width={15} height={15} />
              </span>
            }
            pageCount={pageCount}
            onPageChange={handlePageClick}
            containerClassName={"pagination"}
            activeClassName={"active"}
            forcePage={currentPage}
          />

          <Select value={currentRowsPerPage} onChange={(e) => handleRowsChange(Number(e.target.value))} width="70px" size="sm">
            {rowsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>

          <Text fontSize="sm" fontWeight="500">
            {actualTotalRecords > 0 ? `Showing ${start}-${end} of ${actualTotalRecords} records` : "No records found"}
          </Text>
        </Flex>
      )}
    </MyDiv>
  );
}

export default CustomTable;
